<template>
    <!-- 포인트 상세내역 조회 Modal -->
    <BaseConfirm
        style="width: 780px"
        :isOpen="isOpen"
        :title="`포인트 ${serviceTypeKor} 요청 상세내역 조회`"
        :isShowCancelBtn="true"
        :isShowAcceptBtn="false"
        :ignore-backdrop-click="true"
        :footerSectionClassName="'base-confirm-footer__point-request-detail-view-popup'"
        @show="showHandler"
        @cancel="closeHandler"
    >
        <SkeletonLoader v-if="isLoading" :height="skeletonHeight" />

        <template v-else>
            <PointRequestDetailPopupSummary
                :serviceType="serviceType"
                :serviceTypeKor="serviceTypeKor"
                v-bind="summaryData"
            />

            <div class="form-input-group">
                <p class="title">지급/회수 형태</p>
                <div class="contents font-bold">
                    {{
                        detailData.type
                            ? getServiceTypeKorName(detailData.type)
                            : '-'
                    }}
                </div>
            </div>

            <div class="form-input-group">
                <p class="title">{{ serviceTypeKor }}사유</p>
                <div class="contents">
                    {{ detailData.description || '-' }}
                </div>
            </div>

            <div
                v-if="serviceType === TargetServiceTypeE.PROVIDE"
                class="form-input-group"
            >
                <p class="title">유효기간</p>
                <div class="contents">
                    {{
                        detailData.expireDate
                            ? formatDateTime24(detailData.expireDate)
                            : '-'
                    }}
                </div>
            </div>

            <div
                v-if="serviceType === TargetServiceTypeE.PROVIDE"
                class="form-input-group"
            >
                <p class="title">지급(예약)일시</p>
                <div class="contents">
                    {{ provideAt }}
                </div>
            </div>

            <div
                v-if="serviceType === TargetServiceTypeE.PROVIDE"
                class="form-input-group"
            >
                <p class="title">미가입자 지급대기기간</p>
                <div class="contents">
                    {{
                        detailData.limitedAt
                            ? formatDateTime24(detailData.limitedAt)
                            : '-'
                    }}
                </div>
            </div>

            <div
                v-if="
                    detailData.status === RequestStatusE.CONFIRM ||
                    detailData.status === RequestStatusE.REJECT ||
                    detailData.status === RequestStatusE.PROCESSING ||
                    detailData.status === RequestStatusE.FINISHED
                "
                class="form-input-group"
            >
                <p class="title">승인/반려 사유</p>
                <div class="contents">
                    {{ detailData.approveDescription || '-' }}
                </div>
            </div>

            <div class="form-input-group">
                <p class="title">{{ serviceTypeKor }}대상</p>
                <div class="contents">
                    {{ detailData.uploadedFileName || '-' }}
                </div>
            </div>

            <div class="form-input-group">
                <p class="title">처리상태</p>
                <div class="contents font-bold">
                    {{
                        detailData.status
                            ? getRequestStatusKorName(detailData.status)
                            : '-'
                    }}
                </div>
            </div>
        </template>

        <!-- NOTE: 승인 상태의 예약지급 건만 요청 취소 가능 -->
        <template
            v-if="
                detailData.status === RequestStatusE.CONFIRM &&
                detailData.type === ServiceTypeE.RESERVE
            "
            #footerButton
        >
            <BaseButton
                class="base-confirm-btn"
                type="button"
                :color="'danger'"
                @click="cancelHandler"
                >요청 취소</BaseButton
            >
        </template>
    </BaseConfirm>
</template>

<script setup lang="ts">
import {
    cancelReservedPointProvideRequest as cancelReservedPointProvideRequestApi,
    getPointProvideRequestDetail as getPointProvideRequestDetailApi,
    getPointRetrieveRequestDetail as getPointRetrieveRequestDetailApi,
} from '@/api/points/point-request-detail-api';
import BaseButton from '@/components/common/input/BaseButton.vue';
import SkeletonLoader from '@/components/common/layout/SkeletonLoader.vue';
import BaseConfirm from '@/components/common/popup/BaseConfirm.vue';
import PointRequestDetailPopupSummary from '@/components/points/request-detail/popup/PointRequestDetailPopupSummary.vue';
import {
    getRequestStatusKorName,
    getServiceTypeKorName,
} from '@/filter/points-filter';
import useGlobalStore from '@/store/global-store';
import {PointRequestDetailResI} from '@/types/points/api/point-request-detail-api-types';
import {
    RequestStatusE,
    RequestStatusT,
    ServiceTypeE,
    ServiceTypeKorT,
    ServiceTypeT,
    TargetServiceTypeE,
    TargetServiceTypeKorE,
    TargetServiceTypeT,
} from '@/types/points/point-keyword-types';
import * as Sentry from '@sentry/vue';
import {AxiosError} from 'axios';
import {storeToRefs} from 'pinia';
import {computed, reactive} from 'vue';
import {formatDateTime24} from 'yeogi-utils/filters/date-time-filter';

// props, emits
interface PropsI {
    isOpen: boolean;
    id: number;
    serviceType: TargetServiceTypeT | '';
    provideType: ServiceTypeKorT | '';
}

const props = withDefaults(defineProps<PropsI>(), {
    isOpen: false,
});

const emits = defineEmits<{
    (e: 'close'): void;
    (e: 'refresh'): void;
}>();

const global = useGlobalStore();

// computed
const serviceTypeKor = computed(() => {
    switch (props.serviceType) {
        case TargetServiceTypeE.PROVIDE:
            return TargetServiceTypeKorE.지급;
        case TargetServiceTypeE.RETRIEVE:
            return TargetServiceTypeKorE.회수;
        default:
            return '';
    }
});

const skeletonHeight = computed(() => {
    if (props.serviceType === TargetServiceTypeE.RETRIEVE) {
        // 회수 상세내역 조회 팝업
        return 450;
    } else {
        // 즉시지급/예약지급 상세내역 조회 팝업
        return 600;
    }
});

const provideAt = computed(() => {
    if (!detailData.provideAt) return '-';

    if (
        detailData.status === RequestStatusE.CONFIRM ||
        detailData.status === RequestStatusE.PROCESSING
    ) {
        return `${formatDateTime24(detailData.provideAt, {
            hasTime: true,
        })} 지급예정`;
    } else if (detailData.status === RequestStatusE.FINISHED) {
        return `${formatDateTime24(detailData.provideAt, {
            hasTime: true,
        })} 지급`;
    } else if (
        detailData.status === RequestStatusE.REQUEST_CANCEL ||
        detailData.status === RequestStatusE.REJECT
    ) {
        return `${formatDateTime24(detailData.provideAt, {hasTime: true})}`;
    } else {
        return '-';
    }
});

// ref, reactive
const {isLoading} = storeToRefs(global);

const summaryData = reactive({
    uploadedCount: 0,
    successCount: 0,
    waitingCount: 0,
    failCount: 0,
    totalPoint: 0,
    totalSuccessPoint: 0,
});

// 상세 데이터 저장
const detailData = reactive({
    id: 0,
    description: '',
    expireDate: '',
    approveDescription: '',
    limitedAt: '',
    provideAt: '',
    type: '' as ServiceTypeT,
    uploadedFileName: '',
    status: '' as RequestStatusT,
});

// methods
const cancelHandler = async () => {
    if (!props.id)
        return alert(
            `포인트 ${serviceTypeKor.value} 요청에 필요한 ID 값이 없어요.`,
        );

    if (
        !(
            // NOTE: 승인 상태의 예약지급 건만 요청 취소 가능
            (
                detailData.status === RequestStatusE.CONFIRM &&
                detailData.type === ServiceTypeE.RESERVE
            )
        )
    )
        return;

    if (
        !confirm(
            `포인트 ${serviceTypeKor.value} 요청을 취소하시겠습니까?\n이대로 진행하시면, 포인트 ${serviceTypeKor.value} 요청이 취소 처리가 됩니다.\n\n※ 변경 사항 없이 팝업창을 닫으시려면, 팝업창 우측 상단의 X를 클릭해 주세요.`,
        )
    )
        return;

    try {
        await cancelReservedPointProvideRequestApi(props.id);

        alert(`포인트 ${serviceTypeKor.value} 요청이 취소되었습니다.`);
        closeHandler();

        emits('refresh');
    } catch (err) {
        const axiosError = err as AxiosError;

        console.error(axiosError);

        Sentry.captureException(axiosError, {
            level: 'error',
            extra: {
                code: axiosError.code,
                status: axiosError.response?.status,
                header: axiosError.config.headers,
                request: axiosError.request,
                response: axiosError.response?.data,
                message: axiosError.message,
                from: 'src/components/points/request-detail/popup/PointRequestDetailViewPopup.vue > cancelHandler',
            },
        });

        window.customAlert(axiosError.message);
    }
};

const showHandler = async () => {
    try {
        // 조회 API
        let response = {} as PointRequestDetailResI;

        if (props.serviceType === TargetServiceTypeE.PROVIDE) {
            // 지급 요청 조회
            response = await getPointProvideRequestDetailApi(props.id);
        } else if (props.serviceType === TargetServiceTypeE.RETRIEVE) {
            // 회수 요청 조회
            response = await getPointRetrieveRequestDetailApi(props.id);
        }

        // summaryData 저장. props 전달용
        summaryData.uploadedCount = response.uploadedCount;
        summaryData.successCount = response.successCount;
        summaryData.waitingCount = response.waitingCount ?? 0;
        summaryData.failCount = response.failCount;
        summaryData.totalPoint = response.totalPoint;
        summaryData.totalSuccessPoint = response.totalSuccessPoint;

        // 상세 데이터 저장
        detailData.approveDescription = response.approveDescription ?? '';
        detailData.description = response.description;
        detailData.expireDate = response.expireDate ?? '';
        detailData.id = response.id;
        detailData.limitedAt = response.limitedAt ?? '';
        detailData.provideAt = response.provideAt ?? '';
        detailData.status = response.status;
        detailData.type = response.type;
        detailData.uploadedFileName = response.uploadedFileName;
    } catch (err) {
        const axiosError = err as AxiosError;

        console.error(axiosError);

        Sentry.captureException(axiosError, {
            level: 'error',
            extra: {
                code: axiosError.code,
                status: axiosError.response?.status,
                request: axiosError.request,
                response: axiosError.response?.data,
                message: axiosError.message,
                from: 'src/components/points/request-detail/popup/PointRequestDetailViewPopup.vue > showHandler',
            },
        });

        window.customAlert(axiosError.message);
    }
};

const closeHandler = () => {
    emits('close');
};

// effect
</script>

<style lang="scss" scoped>
.form-input-group {
    display: flex;

    & + & {
        margin-top: 20px;
        padding-top: 20px;
        border-top: 1px solid #ebebeb;
    }

    .title {
        position: relative;
        flex: 1 25%;
        font-weight: 600;
        word-break: keep-all;

        &.required {
            &::after {
                margin-left: 1px;
                content: '*';
                color: $danger;
                line-height: 100%;
                font-size: 10px;
                vertical-align: super;
            }
        }
    }

    .contents {
        flex: 1 75%;
    }
}
</style>
